import D1Asset from '@/editor/assets/d1.asset';
import { guess } from 'web-audio-beat-detector';
import { evaluateVolumeClipEnvelope } from '@/editor/clipEnvelopes'
// import WaveSurfer from 'wavesurfer.js'

export default class AudioAsset extends D1Asset {
  constructor(uuid) {
    super(uuid);
    this._type = 'audio';

    this._props = {
      ...this._props,
      volume: 100,
    };
    this._playing = false
  }

  get resizable() { return false }

  async loadFromFile(file) {
    await super.loadFromFile(file);
    this._asset.meta.name = file.name;
    // const audio = await this.prepareAudio()
    await this.parseAudio();
    this._asset.ready = true;
    this.emit('ready');
  }

  async ab2img(audioBuffer, samplesPerSec = 15) {
    const channels = []
    // this.dbg.log('ab2img', audioBuffer, audioBuffer.getChannelData)
    channels[0] = audioBuffer.getChannelData(0)
    if (audioBuffer.numberOfChannels > 1) {
      channels[1] = audioBuffer.getChannelData(1)
    }
    // We store the per-window PEAK (bold waveform) and RMS (subtle fill) for each channel.
    // sampled[ch][i] => peak amplitude in [0..1]
    // sampledRms[ch][i] => RMS amplitude in [0..1]
    const sampled = channels.map(() => [])
    const sampledRms = channels.map(() => [])
    // NOTE: blocks is the number of samples (columns) we want in the thumbnail.
    // It must span the full buffer duration; previous code divided by samplesPerSec twice,
    // which caused us to only sample the first ~duration/samplesPerSec seconds, then stretch.
    const blocks = Math.max(1, Math.floor(audioBuffer.duration * samplesPerSec))
    const blockSize = Math.max(1, Math.floor(audioBuffer.length / blocks))

    // Dev-only sanity: ensure our last index reaches close to the end of the buffer.
    try {
      if (typeof window !== 'undefined' && window.vybyTimingDebug) {
        const lastIdx = (blocks - 1) * blockSize
        const coverage = audioBuffer.length > 0 ? lastIdx / audioBuffer.length : 0
        if (coverage < 0.85) {
          console.warn('[AudioAsset.ab2img] low coverage (waveform may be time-wrong)', {
            blocks,
            blockSize,
            length: audioBuffer.length,
            duration: audioBuffer.duration,
            samplesPerSec,
            coverage,
          })
        }
      }
    } catch (e) { /* noop */ }

    for (let i = 0; i < blocks; i++) {
      const start = i * blockSize
      const end = Math.min(audioBuffer.length, start + blockSize)
      const count = Math.max(1, end - start)
      for (let ch = 0; ch < channels.length; ch++) {
        const buf = channels[ch]
        let peak = 0
        let sumSq = 0
        for (let j = start; j < end; j++) {
          const v = buf[j] || 0
          const abs = Math.abs(v)
          if (abs > peak) peak = abs
          sumSq += v * v
        }
        const rms = Math.sqrt(sumSq / count)
        sampled[ch].push(peak)
        sampledRms[ch].push(rms)
      }
    }
    const canvas = document.createElement('canvas')
    canvas.width = sampled[0].length
    canvas.height = 100
    const chColor = ['0,0,255', '0,255,0', '0,0,255']
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = 'rgba(0, 0, 0, 0)'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    for (let i = 0; i < sampled[0].length; i++) {
      for (let ch = 0; ch < sampled.length; ch++) {
        const peak = Math.max(0, Math.min(1, Number(sampled[ch][i]) || 0))
        const rms = Math.max(0, Math.min(1, Number(sampledRms[ch][i]) || 0))

        // Draw peak as the bold/obvious bar
        ctx.fillStyle = `rgba(${chColor[ch]}, 0.8)`
        const peakH = peak * 96
        ctx.fillRect(i, 50 - peakH / 2, 1, peakH)

        // Draw RMS as subtle inner bar (never larger than peak)
        const inner = Math.min(rms, peak)
        ctx.fillStyle = `rgba(${chColor[ch]}, 0.25)`
        const rmsH = inner * 70
        ctx.fillRect(i, 50 - rmsH / 2, 1, rmsH)
      }
    }
    const thumbnail = await this.canvas2blob(canvas)
    return {
      sampled,
      sampledRms,
      thumbnail,
      sps: samplesPerSec
    }
  }

  async parseAudio() {
    window.AudioContext = window.AudioContext || window.webkitAudioContext;
    const audioContext = new AudioContext();
    // let currentBuffer = null
    const arrayBuffer = await this._asset.blob.arrayBuffer();
    let audioBuffer = null
    try {
      audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
    } catch (err) {
      this.dbg.warn('decodeAudioData failed; treating as silent asset', err)
      this._asset.meta.duration = 0
      this._asset.meta.bpm = 0
      this._asset.meta.tempo = 0
      this._asset.meta.offset = 0
      this._asset.audioBuffer = null
      this._asset.thumbnail = null
      this._asset.sampled = [[]]
      return
    }
    this._asset.meta.duration = audioBuffer.duration;
    // Beat detection can fail for some sources; default to zeros on error
    try {
      const { bpm, tempo, offset } = await guess(audioBuffer, {
        maxTempo: 190,
        minTempo: 50,
      });
      this._asset.meta.bpm = bpm || 0
      this._asset.meta.tempo = tempo || 0
      this._asset.meta.offset = offset || 0
    } catch (e) {
      this._asset.meta.bpm = 0
      this._asset.meta.tempo = 0
      this._asset.meta.offset = 0
      this.dbg.warn('beat detection failed; using defaults', e)
    }
    this._asset.audioBuffer = audioBuffer;
    // Build overview/sample images with guards
    let main = null
    try { main = await this.ab2img(audioBuffer) } catch (e) { this.dbg.warn('ab2img failed', e); main = { sampled: [[]], thumbnail: null, sps: 15 } }
    // Optional bands; guard each step
    try { this._asset.lowf = await this.collectFrequency(audioBuffer, { lpf: 500 }) } catch (e) { this._asset.lowf = null }
    try { this._asset.low = this._asset.lowf ? await this.ab2img(this._asset.lowf) : null } catch (e) { this._asset.low = null }
    // this.img2tlp(low.thumbnail)
    try { this._asset.midf = await this.collectFrequency(audioBuffer, { lpf: 2000, hpf: 500 }) } catch (e) { this._asset.midf = null }
    try { this._asset.mid = this._asset.midf ? await this.ab2img(this._asset.midf) : null } catch (e) { this._asset.mid = null }
    // this.img2tlp(mid.thumbnail)

    try { this._asset.highf = await this.collectFrequency(audioBuffer, { hpf: 2000 }) } catch (e) { this._asset.highf = null }
    try { this._asset.high = this._asset.highf ? await this.ab2img(this._asset.highf) : null } catch (e) { this._asset.high = null }
    // this.img2tlp(high.thumbnail)

    // console.log({low})
    this._asset.thumbnail = (main && main.thumbnail) || null
    this._asset.sampled = (main && Array.isArray(main.sampled)) ? main.sampled : [[]]
    this._asset.sampledRms = (main && Array.isArray(main.sampledRms)) ? main.sampledRms : null
    // this.dbg.log('sampled', main.sampled)
  }

  canvas2blob(canvas, type = 'image/png') {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob)
      }, type)
    })
  }

  img2tlp(src) {
    const img = document.createElement('img')
    img.src = URL.createObjectURL(src)
    document.getElementById('tlp').appendChild(img)
  }

  dbAt(time) {
    const ret = {
      mid: 0,
      low: 0,
      high: 0,
      main: 0
    }
    const sampled = (this._asset && this._asset.sampled) || null
    if (!Array.isArray(sampled) || sampled.length === 0) return ret
    const ti = Math.max(0, Math.floor(Number(time) || 0) * 15)
    const len0 = (Array.isArray(sampled[0]) || sampled[0] instanceof Float32Array) ? sampled[0].length : 0
    if (!Number.isFinite(ti) || ti < 0 || ti >= len0) return ret
    const channels = []
    for (let i = 0; i < sampled.length; i++) channels.push(i)

    this.dbg.log('dbAt', time, ti)
    ret.main = channels.map(ch => {
      const arr = sampled[ch]
      return (arr && Number.isFinite(arr[ti])) ? arr[ti] : 0
    })
    const pick = (band) => {
      try {
        const arr = band && band.sampled && band.sampled[0]
        return (arr && Number.isFinite(arr[ti])) ? arr[ti] : 0
      } catch (e) { return 0 }
    }
    ret.low = pick(this._asset.low)
    ret.mid = pick(this._asset.mid)
    ret.high = pick(this._asset.high)
    return ret
  }

  collectFrequency(abuff, { lpf, lps, hpf, hps }) {
    return new Promise(resolve => {
      console.log('CF', abuff, { lpf, lps, hpf, hps })
      if (!abuff) {
        return resolve()
      }
      // Create offline context
      const offlineContext = new OfflineAudioContext(1, abuff.length, abuff.sampleRate);

      // Create buffer source
      const source = offlineContext.createBufferSource();
      source.buffer = abuff;

      let last = source

      if (lpf) {
        const filter = offlineContext.createBiquadFilter();
        filter.type = 'lowpass'
        filter.frequency.value = lpf
        last.connect(filter)
        last = filter
      }
      if (lps) {
        const filter = offlineContext.createBiquadFilter();
        filter.type = 'lowshelf'
        filter.frequency.value = lps
        last.connect(filter)
        last = filter
      }
      if (hpf) {
        const filter = offlineContext.createBiquadFilter();
        filter.type = 'highpass'
        filter.frequency.value = hpf
        last.connect(filter)
        last = filter
      }
      if (hps) {
        const filter = offlineContext.createBiquadFilter();
        filter.type = 'highshelf'
        filter.frequency.value = hps
        last.connect(filter)
        last = filter
      }

      // Pipe the song into the filter, and the filter into the offline context
      // source.connect(filter);
      last.connect(offlineContext.destination);

      // Act on the result
      offlineContext.oncomplete = function(e) {
        // Filtered buffer!
        var filteredBuffer = e.renderedBuffer;
        resolve(filteredBuffer)
      }

      // Schedule the song to start playing at time:0
      source.start(0);
      // Render the song
      offlineContext.startRendering()
    })
  }

  color() {
    return '#225b65';
  }

  get PROPS() {
    return {
      volume: {
        type: 'number',
        name: 'Volume',
        normalized: true,
        tool: 'VolumeSlider',
      },
    };
  }

  initAnimation(settings, zf) {
    const animation = super.initAnimation(settings, zf)
    this.dbg.log('animation', animation)
    if (animation.keyframes[0]) {
      animation.keyframes[0].props.volume = 1
      animation.keyframes[0].delay.volume = 0
      animation.keyframes[0].easing.volume = 'Linear.None'
    }
    // animation.keyframes[1].props.volume = 1
    // animation.keyframes[1].delay.volume = this._asset.meta.duration
    // animation.keyframes[1].easing.volume = 'Linear.None'
    if (animation.keyframes[1]) {
      animation.keyframes[1].props.volume = 1
    }
    return animation;
  }

  updateFrame(time, localTime, frame, zf, play = false) {
    const props = super.updateFrame(time, localTime, frame);
    if (frame?.strip) {
      let baseGain = Number(props.volume)
      if (!Number.isFinite(baseGain)) baseGain = 1
      if (baseGain > 1) baseGain = baseGain / 100
      props.volume = evaluateVolumeClipEnvelope(frame.strip, localTime, baseGain).gain
    }
    this.dbg.log('updateFrame?', { time, localTime, play })
    if (localTime < 0 || localTime > frame.strip.length)  {
      if (frame.controller && this._playing) {
        // frame.controller.stop()
        frame.controller.pause()
        this._playing = false
      }
      return props;
    }
    // this.dbg.log('update frame audio', time, localTime, props, zf, play);
    try {
      const levels = this.dbAt(localTime)
      this.emit('levels', levels)
    } catch (e) { /* guard levels emission */ }
    if (frame.controller) {
      // Normalize volume to [0,1] whether props.volume is 0..1 or 0..100
      let v = Number(props.volume)
      if (!Number.isFinite(v)) v = 1
      if (v > 1) v = v / 100
      v = Math.max(0, Math.min(1, v))
      frame.controller.volume = v
      if (!play) {
        if (this._playing) {
          // frame.controller.stop()
          frame.controller.pause()
          this._playing = false
          this.dbg.log('STOP!')
          // frame.controller.emit('reset')
        }
        // frame.controller.currentTime = localTime;
      } else {
        this.dbg.log('play! isPlaying?', this._playing)
        if (!this._playing) {
          // frame.controller.start()
          frame.controller.play()
          //.then((res) => {
            // this.dbg.log('start res', res)
          // })
          this._playing = true
          this.dbg.log('PLAY!')
        }
      }
    }
    // this.applyProps(props)
    return props;
  }

  toolUpdate(update) {
    const ret = super.toolUpdate(update);
    if (update.tool === 'VolumeSlider') {
      ret.volume = update.volume;
    }
    return ret;
  }

  icon() {
    return ['fas', 'volume-off']
  }
}
